import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { GettingStartedProgressDto } from './useGettingStartedOnboarding';
import { QueryKey } from '@/Config/api/queryKey';
import { SocketKey } from '@/Config/api/socketKey';
import { usePreview } from '@/Domain/components/PreviewProvider';
import { useSocket } from '@/Domain/components/SocketProvider';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import {
  setSiteMapPending,
  SITEMAP_CONSUMED_KEY,
} from '@/UI/KnowledgeBase/SiteScrapeReview/siteMapFlags';

/**
 * Refresh-resume signal: when the cached progress row shows
 * `knowledge_base_step_completed=true` and `automatic_flow_completed=false`,
 * the backend scrape completed in a previous session and the user was
 * somewhere in the post-step wizard (summary / external_apps / deployment)
 * when they reloaded. The hook initializes to `completed` in that case so
 * they resume inside the wizard instead of seeing the URL form again.
 *
 * The `knowledge_base_step_completed` flag is co-opted here because in the
 * automatic flow the scrape IS the knowledge-base setup — the user hands us
 * a URL, we ingest it, and the agent is configured off it. That makes the
 * existing flag semantically accurate for the automatic path without needing
 * a new column.
 */
function isAutomaticResumeable(progress: GettingStartedProgressDto | null | undefined): boolean {
  if (!progress) return false;
  return progress.knowledge_base_step_completed && !progress.automatic_flow_completed;
}

// Falls back to a substring on unparseable input so `url_host` stays non-null
// in PostHog (real events were landing with null and breaking funnel joins).
export function extractUrlHost(url: string): string | undefined {
  if (!url || !url.trim()) return undefined;
  try {
    return new URL(url.startsWith('http') ? url : `https://${url}`).hostname;
  } catch {
    const fallback = url
      .replace(/^[a-zA-Z][a-zA-Z0-9+\-.]*:\/\//, '')
      .split(/[/?#]/, 1)[0]
      .trim()
      .toLowerCase();
    return fallback || '-invalid-';
  }
}

// Django Ninja serializes backend failures as `{ errors: { root: [{ message, code }] } }`
// (see `ErrorResponse.from_message` in `api/errors/models.py`). Pull out that
// message first so the user sees e.g. the 429 rate-limit copy; fall back to
// axios's own `error.message` ("Network Error", "Request failed with status
// code 500") for non-API failures.
interface ApiErrorResponse {
  errors?: {
    root?: { message: string; code?: string }[];
  };
}

// Coarse-grained error classes attached to `getting_started_automatic_failed`
// for filtering. `network` is the important one — it specifically captures
// the case where the POST never reached our backend (axios `Network Error`
// — DNS, CORS, dropped connection, gateway/edge timeout). Without this
// classification, a network-layer failure looks identical in PostHog to a
// 5xx returned by our own service, even though the diagnostic path is
// totally different. Internal-only — the UI doesn't read it.
type AutoOnboardingErrorClass = 'network' | 'http_4xx' | 'http_5xx' | 'unknown' | 'backend';

// User-facing error categories. Drives the localized message shown in the
// error view. Orthogonal to `error_class` (WHERE the failure happened) — the
// category answers WHY, from the user's perspective. Backend emits these for
// background-task failures; frontend derives them for network/HTTP failures.
export type AutoOnboardingErrorCategory =
  | 'scrape_failed' // URL couldn't be read (invalid, private, blocking automation)
  | 'timeout' // backend job exceeded its time budget
  | 'dns_failure' // hostname did not resolve (typo, expired domain, DNS not propagated)
  | 'website_not_supported' // Firecrawl returned 403 — host is blocklisted; retry is futile
  | 'bad_request' // 4xx, server rejected the URL itself
  | 'server_error' // 5xx or background-task internal failure
  | 'network' // browser never reached the backend
  | 'unknown'; // catch-all, same copy as server_error

interface ClassifiedError {
  message: string | null;
  errorClass: AutoOnboardingErrorClass;
  errorCategory: AutoOnboardingErrorCategory;
  httpStatus: number | undefined;
}

function classifyMutationError(error: unknown): ClassifiedError {
  if (error instanceof AxiosError) {
    const backendMessage = (error.response?.data as ApiErrorResponse | undefined)?.errors?.root?.[0]
      ?.message;
    const message =
      typeof backendMessage === 'string' && backendMessage.length > 0
        ? backendMessage
        : error.message || null;
    const status = error.response?.status;
    let errorClass: AutoOnboardingErrorClass;
    let errorCategory: AutoOnboardingErrorCategory;
    if (!error.response) {
      // No response = the request failed before getting a status. Axios sets
      // `message = 'Network Error'` for this. Treat it as `network` regardless
      // of the literal so it survives axios version bumps and i18n.
      errorClass = 'network';
      errorCategory = 'network';
    } else if (status && status >= 500) {
      errorClass = 'http_5xx';
      errorCategory = 'server_error';
    } else if (status && status >= 400) {
      errorClass = 'http_4xx';
      errorCategory = 'bad_request';
    } else {
      errorClass = 'unknown';
      errorCategory = 'unknown';
    }
    return { message, errorClass, errorCategory, httpStatus: status };
  }
  const message = error instanceof Error ? error.message || null : null;
  return { message, errorClass: 'unknown', errorCategory: 'unknown', httpStatus: undefined };
}

// Narrows a free-form string from the backend WS payload into a known
// category. Unknown strings (or missing payloads — e.g. older backends) fall
// back to `unknown` so the view layer can render the generic friendly copy.
function parseBackendCategory(raw: string | undefined | null): AutoOnboardingErrorCategory {
  if (raw === 'scrape_failed' || raw === 'timeout' || raw === 'dns_failure') return raw;
  if (raw === 'website_not_supported') return raw;
  if (raw === 'server_error' || raw === 'bad_request' || raw === 'network') return raw;
  return 'unknown';
}

export type AutoOnboardingStep = {
  step: string;
  // Most fields are strings, but the `completed` event's `summary` is
  // `SummaryItem[]`. Consumers that need that shape cast through `unknown`.
  data?: Record<string, string>;
};

export type AutoOnboardingState = 'idle' | 'processing' | 'completed' | 'error';

export interface UseAutomaticOnboardingResult {
  state: AutoOnboardingState;
  steps: AutoOnboardingStep[];
  /**
   * The user-facing category for the current failure. Drives which localized
   * string the error view renders. `null` outside the `error` state.
   *
   * Deliberately NOT a raw error message — the backend sends `raw_message`
   * separately for logs/PostHog, but we never render it (PR fixing the
   * Firecrawl-error leak). Previous `errorMessage` field was removed for the
   * same reason — consumers should read `errorCategory` only.
   */
  errorCategory: AutoOnboardingErrorCategory | null;
  urlType: 'shopify' | 'generic' | null;
  submittedUrl: string | null;
  submitUrl: (url: string) => void;
  isSubmitting: boolean;
  reset: () => void;
}

export function useAutomaticOnboarding(): UseAutomaticOnboardingResult {
  const queryClient = useQueryClient();
  // On fresh mount, consult the cached progress row. If the scrape previously
  // completed (kb_step flag set) and the user hasn't finished the wizard yet,
  // rehydrate into `completed` so the component renders the post-step wizard
  // instead of the URL form. The live WS event stream is gone — the summary
  // card degrades to its minimal "ready" branch via the `steps.length === 0`
  // fallback inside `AutomaticOnboarding`.
  const cachedProgress = queryClient.getQueryData<GettingStartedProgressDto | null>([
    QueryKey.gettingStarted.progress,
  ]);
  const resumed = useRef(isAutomaticResumeable(cachedProgress)).current;

  const [state, setState] = useState<AutoOnboardingState>(resumed ? 'completed' : 'idle');
  const [steps, setSteps] = useState<AutoOnboardingStep[]>([]);
  const [errorCategory, setErrorCategory] = useState<AutoOnboardingErrorCategory | null>(null);
  const [urlType, setUrlType] = useState<'shopify' | 'generic' | null>(null);
  const [submittedUrl, setSubmittedUrl] = useState<string | null>(null);
  const { socket } = useSocket();
  const { refreshConfiguration } = usePreview();
  const { capture } = usePostHogAnalytics();
  const stateRef = useRef(state);
  stateRef.current = state;
  // Running count of `setting_applied` events since the last submit. Reset in
  // `submitUrl` alongside the other per-run state so a retry starts at zero.
  const settingsAppliedCountRef = useRef(0);
  // Snapshot of the URL + detected type for this run so we can attach them to
  // the terminal completed/failed PostHog events. Socket events don't carry
  // the URL back, and `urlType` is state that may not have flushed by the time
  // `completed` arrives — refs sidestep both problems.
  const submittedUrlRef = useRef<string | null>(null);
  const urlTypeRef = useRef<'shopify' | 'generic' | null>(null);

  // Persist the "scrape done" signal on the progress row so a page refresh
  // drops the user back into the post-step wizard. Uses the shared mutation
  // defaults (see queryClient.ts) so we just feed it a delta. On success we
  // merge only the fields we owned into the cache rather than overwrite with
  // the full server response — another concurrent PATCH (e.g. the
  // deployment-step Finish click) may have updated the row between when this
  // request was dispatched and when the server echoed its view back, and a
  // full overwrite would regress those newer fields.
  const progressMutation = useMutation<
    GettingStartedProgressDto,
    Error,
    Partial<GettingStartedProgressDto>
  >({
    mutationKey: [QueryKey.gettingStarted.progress],
    onSuccess: (_updated, variables) => {
      queryClient.setQueryData<GettingStartedProgressDto | null>(
        [QueryKey.gettingStarted.progress],
        (prev) => (prev ? { ...prev, ...variables } : prev),
      );
    },
  });

  // Listen for websocket progress events
  useEffect(() => {
    const handler = (event: { step: string; data?: Record<string, string> }) => {
      // Accept events only when we've kicked the backend job off (idle right
      // after submit, processing while it runs). Once we've reached `completed`
      // or `error`, late events from a retry of a previous run must be ignored
      // so they don't overwrite the current state machine.
      const isAcceptingEvents = stateRef.current === 'processing' || stateRef.current === 'idle';
      if (!isAcceptingEvents) return;

      if (event.step === 'started') {
        setState('processing');
      }

      if (event.step === 'url_type_detected' && event.data?.url_type) {
        const detected = event.data.url_type as 'shopify' | 'generic';
        setUrlType(detected);
        urlTypeRef.current = detected;
      }

      if (event.step === 'setting_applied') {
        settingsAppliedCountRef.current += 1;
        // Refresh sidebar when agent name or avatar changes
        if (event.data?.setting === 'ai_agent_name' || event.data?.setting === 'avatar') {
          queryClient.invalidateQueries({ queryKey: [QueryKey.aiAssistants.get] });
        }
      }

      if (event.step === 'error') {
        setState('error');
        // Backend sends `{category, raw_message}` — category drives the
        // localized copy, raw_message stays out of the UI entirely. Older
        // backends that sent `{message}` fall through to `unknown`; that's
        // fine since the copy is the same friendly fallback.
        const category = parseBackendCategory(event.data?.category);
        setErrorCategory(category);
        // `error_class: 'backend'` distinguishes a WS failure (request reached
        // the server, the background task failed) from the mutation onError
        // paths below (`network` / `http_*`). Same event name; the property
        // tells you where in the stack it died. `error_message` carries the
        // raw backend string for our own debugging in PostHog — never rendered.
        const rawMessage = event.data?.raw_message ?? event.data?.message ?? '';
        capture(PostHogEventEnum.GettingStartedAutomaticFailed, {
          error_message: rawMessage,
          error_class: 'backend',
          error_category: category,
          url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
        });
        return;
      }

      if (event.step === 'completed') {
        // Invalidate caches so the widget reads fresh config when it eventually mounts
        queryClient.invalidateQueries({ queryKey: ['knowledge_base'] });
        queryClient.invalidateQueries({ queryKey: [QueryKey.widgetSettings] });
        queryClient.invalidateQueries({ queryKey: [QueryKey.knowledgeBase.articlesInfinite] });
        queryClient.invalidateQueries({ queryKey: [QueryKey.aiAssistants.get] });
        // AiPreview's widget iframe is kept alive by KeepAliveRenderer in
        // DefaultLayout, so it doesn't remount on navigation to AiPreview and
        // would keep displaying the pre-onboarding default config. Tell it to
        // refetch /configuration so the customized values from onboarding
        // appear without a manual page/conversation refresh.
        refreshConfiguration();
        setState('completed');
        // Flip the "scrape done" flag on the progress row so a refresh lands
        // back inside the post-step wizard rather than the URL form.
        progressMutation.mutate({ knowledge_base_step_completed: true });
        // Flag the background site map so the status dock opens the review modal once
        // ready. Shopify onboarding never maps (no Step-11 site map), so don't flag it:
        // a dead pending flag would keep the dock polling an idle 404 for nothing.
        if (urlTypeRef.current !== 'shopify') {
          localStorage.removeItem(SITEMAP_CONSUMED_KEY);
          setSiteMapPending();
          capture(PostHogEventEnum.SiteScrapeMapStarted, {
            source: 'onboarding_auto',
            url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
          });
        }
        capture(PostHogEventEnum.GettingStartedAutomaticCompleted, {
          url_type: urlTypeRef.current,
          settings_count: settingsAppliedCountRef.current,
          url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
        });
      }

      setSteps((prev) => [...prev, { step: event.step, data: event.data }]);
    };

    socket.on(SocketKey.onboarding.fromUrlProgress, handler);
    return () => {
      socket.off(SocketKey.onboarding.fromUrlProgress, handler);
    };
  }, [socket, queryClient, refreshConfiguration, capture, progressMutation]);

  const mutation = useMutation({
    mutationKey: [QueryKey.onboarding.fromUrl],
    // `submitUrl` flips state to `processing` synchronously before the mutation
    // fires, so a POST failure (rate-limit 429, 400 invalid URL, 5xx) would
    // otherwise strand the UI on the progress card forever — no WS events
    // arrive because the backend job was never dispatched. Mirror the WS
    // `error` handler: transition to `error`, surface the backend message
    // (falling back to null so the view can localize), and fire the same
    // PostHog failure event. The `isAcceptingEvents` guard in the WS handler
    // then drops any stale events from a prior run.
    onError: (error) => {
      const {
        message,
        errorClass: cls,
        errorCategory: cat,
        httpStatus,
      } = classifyMutationError(error);
      setState('error');
      setErrorCategory(cat);
      capture(PostHogEventEnum.GettingStartedAutomaticFailed, {
        error_message: message ?? '',
        error_class: cls,
        error_category: cat,
        // Only attach when present — undefined fields are dropped by PostHog so
        // the network-failure case doesn't pollute the property with a literal.
        http_status: httpStatus,
        url_host: submittedUrlRef.current ? extractUrlHost(submittedUrlRef.current) : undefined,
      });
    },
  });

  const submitUrl = useCallback(
    (url: string) => {
      setState('processing');
      setSteps([]);
      setErrorCategory(null);
      setUrlType(null);
      setSubmittedUrl(url);
      settingsAppliedCountRef.current = 0;
      submittedUrlRef.current = url;
      urlTypeRef.current = null;
      // Commit the user's mode choice to the DB. The UI normalizes `choice`
      // to `automatic` so this looks identical client-side, but production
      // funnel queries that filter by `mode='automatic'` were silently
      // missing every automatic-flow user (their DB row stayed `choice`).
      progressMutation.mutate({ mode: 'automatic' });
      // Deliberately NOT pre-registering the URL in the upload store: the
      // bottom-right scrape progress widget would compete for attention with
      // the onboarding flow itself. Once the user clicks through onboarding
      // and lands on the KB page, the page-header bar and sidebar warning
      // icon (driven by useKbUsageSocket → /allowed_features) surface the
      // KB-limit state if the post-onboarding sitewide crawl saturates the
      // plan limit — a quieter, contextual signal than a transient pill.
      capture(PostHogEventEnum.GettingStartedAutomaticSubmitted, {
        url_host: extractUrlHost(url),
      });
      mutation.mutate({ url });
    },
    [mutation, progressMutation, capture],
  );

  const reset = useCallback(() => {
    setState('idle');
    setSteps([]);
    setErrorCategory(null);
    setUrlType(null);
  }, []);

  return {
    state,
    steps,
    errorCategory,
    urlType,
    submittedUrl,
    submitUrl,
    isSubmitting: mutation.isPending,
    reset,
  };
}
